import { Gender, MonsterTypes } from './types';
import { MovesGenerator } from './moves';
import { TraitsGenerator } from './traits';
import { PointsPerProperty } from './constants';
import { pickRandom } from './util';
export default class MonsterGenerator {
    static generateFromCode(code, level) {
        const [traitsChunk, numMovesChunk, movesChunk, typesChunk, genderChunk, geneticStrengthChunk, canEvolveChunk, attributesChunk,] = code.split(MonsterGenerator.propertyDelimiter);
        const monster = {
            baseAttributes: null,
            gender: null,
            type: null,
            subType: null,
            moves: null,
            numMoves: null,
            traits: null,
            geneticStrength: null,
            canEvolve: null,
        };
        monster.traits = TraitsGenerator.generateFromCode(traitsChunk);
        const { numMoves, moves } = MovesGenerator.generateFromCode(numMovesChunk, movesChunk);
        monster.numMoves = numMoves;
        monster.moves = moves;
        const { type, subType } = MonsterGenerator.generateTypesFromCode(typesChunk);
        monster.type = type;
        monster.subType = subType;
        monster.gender = MonsterGenerator.generateGenderFromCode(genderChunk);
        monster.geneticStrength = MonsterGenerator.generateGeneticStrengthFromCode(geneticStrengthChunk);
        monster.canEvolve = MonsterGenerator.genrateCanEvolveFromCode(canEvolveChunk);
        monster.baseAttributes = MonsterGenerator.generateAttributesFromCode(attributesChunk);
        return monster;
    }
    static generateCode(remainingPoints = MonsterGenerator.generateTotalPoints()) {
        let code = '';
        const [traitsCode, traitsWeight] = MonsterGenerator.generateTraits(remainingPoints);
        remainingPoints -= traitsWeight;
        code += traitsCode;
        code += MonsterGenerator.propertyDelimiter;
        const [numMovesCode, numMovesWeight] = MonsterGenerator.generateMoveCount(remainingPoints);
        remainingPoints -= numMovesWeight;
        code += numMovesCode;
        code += MonsterGenerator.propertyDelimiter;
        const [movesCode, movesWeight] = MovesGenerator.generateMoves(remainingPoints);
        remainingPoints -= movesWeight;
        code += movesCode;
        code += MonsterGenerator.propertyDelimiter;
        const [typesCode, typesWeight] = MonsterGenerator.generateTypes(remainingPoints);
        remainingPoints -= typesWeight;
        code += typesCode;
        code += MonsterGenerator.propertyDelimiter;
        const [genderCode, genderWeight] = MonsterGenerator.generateGender(remainingPoints);
        remainingPoints -= genderWeight;
        code += genderCode;
        code += MonsterGenerator.propertyDelimiter;
        const [geneticStrengthCode, geneticStrengthWeight] = MonsterGenerator.generateGeneticStrength(remainingPoints);
        remainingPoints -= geneticStrengthWeight;
        code += geneticStrengthCode;
        code += MonsterGenerator.propertyDelimiter;
        const [canEvolveCode, canEvolveWeight] = MonsterGenerator.generateCanEvolve(remainingPoints);
        remainingPoints -= canEvolveWeight;
        code += canEvolveCode;
        code += MonsterGenerator.propertyDelimiter;
        const [attributeCode, attributeWeight] = MonsterGenerator.generateAttributes(remainingPoints);
        remainingPoints -= attributeWeight;
        code += attributeCode;
        return code;
    }
    static generateTotalPoints() {
        return 70 + Math.floor(Math.random() * 30);
    }
    static generateTraits(maxPoints) {
        const possibleRolls = [
            [1, 50],
            [2, 35],
            [3, 15],
        ];
        const numTraits = pickRandom(possibleRolls.filter(([num]) => {
            return num * PointsPerProperty.Traits < maxPoints;
        }));
        const traitsWeight = PointsPerProperty.Traits * numTraits;
        const traitsCode = TraitsGenerator.generateTraitsCode(numTraits);
        return [traitsCode, traitsWeight];
    }
    static generateMoveCount(maxPoints) {
        const possibleRolls = [
            [3, 50],
            [4, 35],
            [5, 15],
        ];
        const numMoves = pickRandom(possibleRolls.filter(([num]) => {
            return num * PointsPerProperty.Moves < maxPoints;
        }));
        const numMovesWeight = PointsPerProperty.Moves * numMoves;
        const numMovesCode = MovesGenerator.generateNumMovesCode(numMoves);
        return [numMovesCode, numMovesWeight];
    }
    static generateTypes(_maxPoints) {
        const possibleRolls = [
            MonsterTypes.Earth,
            MonsterTypes.Water,
            MonsterTypes.Fire,
            MonsterTypes.Electric,
            MonsterTypes.Flying,
            MonsterTypes.Physical,
        ];
        const mainType = pickRandom(possibleRolls);
        const subType = pickRandom(possibleRolls);
        const typesCode = MonsterGenerator.generateTypesCode(mainType, subType);
        return [typesCode, 0];
    }
    static generateTypesFromCode(typesChunk) {
        const { [MonsterGenerator.typeKey]: type, [MonsterGenerator.subTypeKey]: subType, } = JSON.parse(typesChunk);
        return { type, subType };
    }
    static generateTypesCode(mainType, subType) {
        return JSON.stringify({
            [MonsterGenerator.typeKey]: mainType,
            [MonsterGenerator.subTypeKey]: subType,
        });
    }
    static generateGender(maxPoints) {
        const possibleRolls = [
            Gender.Male,
            Gender.Female,
        ];
        const gender = pickRandom(possibleRolls);
        const genderCode = MonsterGenerator.generateGenderCode(gender);
        return [genderCode, 0];
    }
    static generateGenderFromCode(genderChunk) {
        const { [MonsterGenerator.genderKey]: gender, } = JSON.parse(genderChunk);
        return gender;
    }
    static generateGenderCode(gender) {
        return JSON.stringify({
            [MonsterGenerator.genderKey]: gender,
        });
    }
    static generateGeneticStrength(maxPoints) {
        const geneticStrength = Math.floor(Math.random() * Math.min(maxPoints, 10));
        const geneticStrengthCode = MonsterGenerator.generateGeneticStrengthCode(geneticStrength);
        const geneticStrengthWeight = PointsPerProperty.GeneticStrength * geneticStrength;
        return [geneticStrengthCode, geneticStrengthWeight];
    }
    static generateGeneticStrengthFromCode(geneticStrengthChunk) {
        const { [MonsterGenerator.geneticStrengthKey]: strength, } = JSON.parse(geneticStrengthChunk);
        return strength;
    }
    static generateGeneticStrengthCode(strength) {
        return JSON.stringify({
            [MonsterGenerator.geneticStrengthKey]: strength,
        });
    }
    static generateAttributes(maxPoints) {
        let attributes = {
            attack: 0,
            defense: 0,
            health: 0,
            magic: 0,
            mana: 0,
            speed: 0,
            luck: 0,
        };
        let totalWeight = 0;
        while (totalWeight < maxPoints) {
            const key = pickRandom(Object.keys(attributes));
            if (attributes[key] < 10) {
                attributes[key]++;
                totalWeight += PointsPerProperty.Attributes;
            }
        }
        const attributesCode = MonsterGenerator.generateAttributesCode(attributes);
        return [attributesCode, totalWeight];
    }
    static generateCanEvolve(maxPoints) {
        const possibleRolls = [
            [false, 85],
            [true, 15],
        ];
        const canEvolve = pickRandom(possibleRolls);
        const canEolveCode = MonsterGenerator.generateCanEvolveCode(canEvolve);
        const canEvolveWeight = canEvolve ? PointsPerProperty.CanEvolve : 0;
        return [canEolveCode, canEvolveWeight];
    }
    static genrateCanEvolveFromCode(canEvolveChunk) {
        const { [MonsterGenerator.canEvolveKey]: canEvolve, } = JSON.parse(canEvolveChunk);
        return canEvolve;
    }
    static generateCanEvolveCode(canEvolve) {
        return JSON.stringify({
            [MonsterGenerator.canEvolveKey]: canEvolve,
        });
    }
    static generateAttributesFromCode(attributesChunk) {
        const { [MonsterGenerator.attributesKey]: attributes, } = JSON.parse(attributesChunk);
        return attributes;
    }
    static generateAttributesCode(attributes) {
        return JSON.stringify({
            [MonsterGenerator.attributesKey]: attributes,
        });
    }
}
MonsterGenerator.typeKey = 'type';
MonsterGenerator.subTypeKey = 'subType';
MonsterGenerator.genderKey = 'gender';
MonsterGenerator.geneticStrengthKey = 'geneticStrength';
MonsterGenerator.canEvolveKey = 'canEvolve';
MonsterGenerator.attributesKey = 'attributes';
MonsterGenerator.propertyDelimiter = '|';
